'use client'
import { useToast } from '@ui-components/toast-card'
import { useFlag, useVariant } from '@unleash/proxy-client-react'
import { PropsWithChildren, useEffect } from 'react'

import { useGetHubSelected, useProfile } from '../auth'
import { useComponentTranslation } from '../i18n'
import { hermesLogger } from '../observability'
import { useLatencyWatcher } from './use-latency-watcher/use-latency-watcher'
import { useOnlineStatus } from './use-online-status/use-online-status'

export const NetworkConnectionProvider = ({ children }: PropsWithChildren) => {
  const t = useComponentTranslation('MainLayout')
  const isOnline = useOnlineStatus()
  const { user } = useProfile()
  const { hubId } = useGetHubSelected()
  const latency = useLatencyWatcher()
  const showLatency = useFlag('hubr.latency-alert')
  const watchLatency = useFlag('hubr.watch-latency')
  const latencyLimit = useVariant('hubr.latency-limit')
  const { toast, closeAll } = useToast()

  const formattedLatency =
    latency !== null ? `${latency.toFixed(2)}ms` : 'Sem conexão'
  const formattedLatencyLimit = latencyLimit.payload
    ? parseInt(latencyLimit.payload?.value)
    : 2000

  if (watchLatency) {
    hermesLogger.info(
      `[Latency] ${formattedLatency} on ${user?.name} in ${hubId} `,
      {
        user,
        hubId,
        formattedLatency,
        latency,
      }
    )
  }

  useEffect(() => {
    if (showLatency && latency && latency > formattedLatencyLimit) {
      toast.warning({
        title: t('Slow connection'),
        description: t('Please check your internet'),
        iconName: 'low-wifi',
      })
    }
  }, [latency, t, toast, formattedLatencyLimit, showLatency])

  useEffect(() => {
    if (!isOnline) {
      toast.error({
        title: t('You are disconnected or without internet'),
        description: t('Please check your internet connection and try again'),
        isClosable: false,
        duration: null,
        iconName: 'error-wifi',
      })
    }

    if (isOnline) {
      closeAll()
    }
  }, [closeAll, isOnline, t, toast])

  return children
}
